import CarouselWithIndex from "components/app/carousel-with-index/CarouselWithIndex";
import VideoHoverPlay from "components/app/video-hover-play/VideoHoverPlay";

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppLog } from "services";
import { RootState, useAppSelector } from "store/store";
import { setLastIndex, setPost, setSecondIndex } from "store/welcomePostSlice";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import Masonry from "react-masonry-css";
import { ScrollArea, ScrollBar } from "components/ui/scroll-area";
import ImageHoverPlay from "components/app/image-hover-play/ImageHoverPlay";

import ContentInfo from "components/app/content-info/ContentInfo";
import StatusHoverPlay from "components/app/status-hover-play/StatusHoverPlay";
import HelmetHtml from "components/app/helmet-html/HelmetHtml";
import ExploreLayout from "layout/explore-layout/ExploreLayout";
import appStore from "store/appStore";
import { Skeleton } from "components/ui/skeleton";

const Explore: React.FC = () => {
  const { onOpenDialogPostModal, onCloseDialogPostModal } = appStore();

  const [record, setRecord] = useState(new AppRecordJK());
  const [itemGroups, setItemGroups] = useState<any[]>([]);

  const dispatch = useDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const { posts } = useAppSelector((state) => state.welcomePost);
  const [selectedProductGroupCode, setSelectedProductGroupCode] = useState<string>("All");
  const selectedProductGroupCodeRef = useRef(selectedProductGroupCode);

  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const postIdRef = useRef<string[]>([]);

  const breakpointColumnsObj = {
    default: 5,
    1536: 5,
    1280: 4,
    1024: 3,
    768: 3,
    425: 2,
  };
  const [loading, setLoading] = useState<any>(false);

  const fetchPost = async ({ isRefresh = false }: { isRefresh?: boolean } = {}) => {
    const updatedRecord = new AppRecordJK({
      ...record,
      loadingStatus: AppRecordStatusJK.loading,
    });
    setLoading(true);
    try {
      if (isRefresh) {
        updatedRecord.loadingStatus = AppRecordStatusJK.loading;
      } else {
        updatedRecord.loadingStatus = AppRecordStatusJK.loadingMore;
      }
      setRecord(updatedRecord);
      let url: string = "";

      if (isAuthenticated) {
        url = RouteApi.getPostWeb;
        url += `token=${token}`;
      } else {
        url = RouteApi.getPostWebGuest;
      }

      const data = {
        post_ids: postIdRef.current,
        tag: "explore",
        category: selectedProductGroupCodeRef.current,
      };

      await RequestApi(url, { data })
        .then((responses) => {
          if (responses.status === "success") {
            const tmpPosts = isRefresh ? [...responses["posts"]] : [...posts, ...responses["posts"]];
            setLoading(false);

            dispatch(setPost(tmpPosts));
            postIdRef.current = responses["posts"].map((morePost: any) => {
              return morePost?.provider_post_id.toString();
            });
            if (responses["posts"]) {
              updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
            } else {
              updatedRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
            }
            setRecord(updatedRecord);
          } else {
            updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
            setRecord(updatedRecord);
          }
        })
        .catch((err) => {
          setLoading(false);
          updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
          setRecord(updatedRecord);
        });
    } catch (error) {
      setLoading(false);

      updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
      setRecord(updatedRecord);

      AppLog.show({
        message: `[fetchPost]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      updatedRecord.loadingStatus = AppRecordStatusJK.loaded;
      setRecord(updatedRecord);
    }
  };

  // Scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1200) {
        // if (record.canLoadMore()) {
        //   fetchPost();
        // }
        if (!loading) {
          fetchPost();
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading]);

  useEffect(() => {
    if (posts.length === 0) {
      // fetchPost({ isRefresh: true });
      fetchPost();
    }
  }, []);

  const handleClickCategory = (groupCode: string) => {
    if (selectedProductGroupCode === groupCode) return;
    setSelectedProductGroupCode(groupCode);
    postIdRef.current = [];
    selectedProductGroupCodeRef.current = groupCode;
    fetchPost({ isRefresh: true });
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("item_groups");
    if (storedUser) {
      setItemGroups(JSON.parse(storedUser));
    }
  }, []);

  const onGoToPost = (postId: string) => {
    onOpenDialogPostModal(postId, "/");
  };

  useEffect(() => {
    const handlePopState = () => {
      onCloseDialogPostModal();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const contentNotAvailable = () => (
    <div className="relative group mb-4 break-inside-avoid rounded-xl h-[400px] bg-black dark:bg-gray-700">
      <div className="w-full h-full flex items-center justify-center text-white">Content not available</div>
    </div>
  );

  const contentNotAvailable1 = () => (
    <div className="relative group mb-4 break-inside-avoid rounded-xl h-[400px] bg-black dark:bg-gray-700">
      <div className="w-full h-full flex items-center justify-center text-white">Test Content</div>
    </div>
  );

  const postContents = (item: any) => {
    if (item) {
      if (item?.media_type === "photo") {
        if (item?.post_attachments.length > 0) {
          return (
            <div className="relative">
              <div onClick={() => onGoToPost(item.provider_post_id)}>
                <ImageHoverPlay post={item} />
              </div>
              <ContentInfo post={item} />
            </div>
          );
        } else {
          return contentNotAvailable();
        }
      } else if (item?.media_type === "album") {
        if (item?.post_attachments.length > 0) {
          return (
            <div className="relative">
              <div onClick={() => onGoToPost(item.provider_post_id)}>
                <CarouselWithIndex item={item} imgClassName="w-full h-auto object-cover rounded-lg cursor-pointer" indexClassName="" />
              </div>
              <ContentInfo post={item} />
            </div>
          );
        } else {
          return contentNotAvailable();
        }
      } else if (item?.media_type === "video") {
        if (item?.post_attachments.length > 0) {
          return (
            <div className="relative">
              <div onClick={() => onGoToPost(item.provider_post_id)}>
                <VideoHoverPlay post={item} videoClassName="rounded-xl" />
              </div>
              <ContentInfo post={item} />
            </div>
          );
        } else {
          return contentNotAvailable();
        }
      } else if (item?.media_type === "status") {
        if (item?.post_attachments.length > 0) {
          return (
            <div className="relative" onClick={() => onGoToPost(item.provider_post_id)}>
              <StatusHoverPlay post={item} />
              <ContentInfo post={item} />
            </div>
          );
        } else {
          return contentNotAvailable();
        }
      }
    }
  };

  return (
    <div className="">
      <HelmetHtml title="FlipFlop Social Meida - Explore" description="" />

      <ExploreLayout>
        <ScrollArea className="w-full grid grid-cols-1 px-2 pb-3 pt-2 mb-2">
          <div className="flex">
            <p
              onClick={() => handleClickCategory("All")}
              className={`p-1 border rounded-xl px-2 mr-2 cursor-pointer font-semibold ${
                selectedProductGroupCode === "All" ? "bg-black text-white dark:bg-white dark:text-black" : " dark:hover:bg-white dark:hover:text-black"
              }`}
            >
              All
            </p>
            {itemGroups?.map((group) => {
              return (
                <div
                  key={`${group?.code}`}
                  className={`p-1 border rounded-xl px-2 mr-2 cursor-pointer font-semibold ${
                    selectedProductGroupCode === group?.code.toString() ? "bg-black text-white dark:bg-white dark:text-black" : " dark:hover:bg-white dark:hover:text-black"
                  }`}
                  onClick={() => handleClickCategory(group?.code.toString())}
                >
                  <p className="text-nowrap">{group?.code}</p>
                </div>
              );
            })}
          </div>
          <ScrollBar className="cursor-grab" orientation="horizontal" />
        </ScrollArea>

        {/* {record.isLoading() ? (
          <>
            <div className="fixed w-full flex justify-center bottom-50 right-0">
              <LoaderCircle size={30} className="animate-spin text-red-400" />
            </div>
          </>
        ) : (
          <>
            <Masonry breakpointCols={breakpointColumnsObj} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
              {[...posts, {}, {}, {}, {}, {}].map((post: any, index: number) => {
                const lastIndex = posts.length;
                // const secondLastIndex = posts.length + 3;

                if (index > lastIndex - 1 && record.isLoadingMore()) {
                  return (
                    <>
                      <Skeleton id={`${post?.id}`} key={index} className="relative group mb-4 break-inside-avoid rounded-xl  h-[400px]">
                        <div className="h-[100%] w-full rounded-t-xl relative">
                          <Skeleton className="h-5 w-[35px] bg-gray-200 dark:bg-gray-700 absolute top-2 left-2" />
                        </div>
                      </Skeleton>
                    </>
                  );
                } else {
                  return (
                    <div id={`${post?.id}`} key={index} className="relative group mb-4 break-inside-avoid bg-white rounded-xl">
                      {postContents(post)}
                    </div>
                  );
                }
              })}
            </Masonry>
          </>
        )} */}
        {/* {loading && "loading..."} */}
        {/* {record.isLoadingMore() && (
          <div className="fixed w-full flex justify-center bottom-50 right-0">
            <LoaderCircle size={30} className="animate-spin text-red-400" />
          </div>
        )} */}

        <Masonry breakpointCols={breakpointColumnsObj} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
          {[...posts, {}, {}, {}, {}, {}].map((post: any, index: number) => {
            const lastIndex = posts.length;

            if (index > lastIndex - 1 && loading) {
              return (
                <div key={index}>
                  <Skeleton id={`${post?.id}`} key={index} className="relative group mb-4 break-inside-avoid rounded-xl  h-[400px]">
                    <div className="h-[100%] w-full rounded-t-xl relative">
                      <Skeleton className="h-5 w-[35px] bg-gray-200 dark:bg-gray-700 absolute top-2 left-2" />
                    </div>
                  </Skeleton>
                </div>
              );
            } else {
              return (
                <div id={`${post?.id}`} key={index} className="relative group mb-4 break-inside-avoid bg-white rounded-xl">
                  {postContents(post)}
                </div>
              );
            }
          })}
        </Masonry>
      </ExploreLayout>
    </div>
  );
};

export default Explore;
